import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

export type ThemePreference = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

const storageKey = 'sleepvue-theme';
const mediaQuery = '(prefers-color-scheme: dark)';

interface ThemeContextValue {
    preference: ThemePreference;
    theme: Theme;
    setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function parsePreference(value: string | null): ThemePreference {
    return value === 'light' || value === 'dark' ? value : 'system';
}

function readPreference(): ThemePreference {
    try {
        return parsePreference(window.localStorage.getItem(storageKey));
    } catch (error) {
        console.warn('Unable to read the saved appearance preference.', error);
        return 'system';
    }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
    const [systemTheme, setSystemTheme] = useState<Theme>(() =>
        window.matchMedia(mediaQuery).matches ? 'dark' : 'light'
    );
    const theme = preference === 'system' ? systemTheme : preference;

    useEffect(() => {
        const media = window.matchMedia(mediaQuery);
        const onChange = () => setSystemTheme(media.matches ? 'dark' : 'light');
        const onStorage = (event: StorageEvent) => {
            if (event.storageArea === window.localStorage && (event.key === storageKey || event.key === null)) {
                setPreferenceState(parsePreference(event.newValue));
            }
        };
        onChange();
        media.addEventListener('change', onChange);
        window.addEventListener('storage', onStorage);
        return () => {
            media.removeEventListener('change', onChange);
            window.removeEventListener('storage', onStorage);
        };
    }, []);

    const setPreference = useCallback((value: ThemePreference) => {
        setPreferenceState(value);
        try {
            window.localStorage.setItem(storageKey, value);
        } catch (error) {
            console.warn('Unable to save the appearance preference. It will apply for this visit only.', error);
        }
    }, []);

    useLayoutEffect(() => {
        document.documentElement.dataset.bsTheme = theme;
        document.documentElement.style.setProperty('color-scheme', theme);
        document.querySelector('meta[name="theme-color"]')?.setAttribute(
            'content', theme === 'dark' ? '#0f172a' : '#4c1d95'
        );
        if (Capacitor.isNativePlatform()) {
            StatusBar.setStyle({ style: Style.Dark }).catch(error =>
                console.warn('Unable to update the status bar appearance.', error)
            );
            StatusBar.setBackgroundColor({ color: theme === 'dark' ? '#0f172a' : '#4c1d95' }).catch(error =>
                console.warn('Unable to update the status bar background.', error)
            );
        }
    }, [theme]);

    const value = useMemo(() => ({ preference, theme, setPreference }), [preference, theme, setPreference]);
    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within ThemeProvider.');
    }
    return context;
}
